import { FormConfig, FormValues, NextFunction } from '@topdanmark-onlinesolutions/claim-form-types';
import { getValueOrDefault } from '../../components/values/Get';
import { getWhatHasBeenDamaged, getWorkshopDetails } from '../shared/vehicle';
import { isCustomerAllowedToSelectWorkshop } from '../../helpers/business-rules/isCustomerAllowedToSelectWorkshop';

export const getNextPage: NextFunction = async (curPageId: string, formConfig: FormConfig, formValues: FormValues) => {
    let nextPage: string | undefined = '';
    switch (curPageId) {
        case 'godt-at-vide':
            nextPage = 'dato';
            break;
        case 'dato':
            nextPage = 'tidspunkt';
            break;
        case 'tidspunkt':
            nextPage = 'adresse';
            break;
        case 'adresse':
            nextPage = 'beskriv-skaden';
            break;
        case 'beskriv-skaden':
            nextPage = 'koeretoej-nummerplade';
            break;
        case 'koeretoej-nummerplade':
            nextPage = 'hvilket-koeretoej';
            break;
        case 'hvilket-koeretoej':
            nextPage = 'ansvar';
            break;
        case 'ansvar':
            nextPage = 'hvad-er-skadet';
            break;
        case 'hvad-er-skadet':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skader-andet-koeretoej':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skadezone':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;

        case 'anden-persons-koeretoej':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'personskade':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'oplysninger-om-passagerer':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skade-ting':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            const isReadyToFileAllThingsDamaged =
                getValueOrDefault(formConfig, formValues, 'areYouReadyToRegister').value === 'yes';
            if (isReadyToFileAllThingsDamaged) {
                nextPage = 'genstandsregistrering';
            }
            break;
        case 'genstandsregistrering':
            nextPage = 'oplysninger-om-modpart';
            break;
        case 'oplysninger-om-modpart':
            if (getValueOrDefault(formConfig, formValues, 'doYouHaveInformationAboutOthersInvolved').value === 'yes') {
                nextPage = 'modpartens-rolle-1';
            } else {
                nextPage = 'foerer';
            }

            break;

        case 'modpartens-rolle-1':
            nextPage = 'om-modparten-1';
            break;
        case 'modpartens-rolle-2':
            nextPage = 'om-modparten-2';
            break;
        case 'modpartens-rolle-3':
            nextPage = 'om-modparten-3';
            break;

        case 'om-modparten-1':
            nextPage = isAddAnotherPersonChecked(
                'doYouHaveInformationAboutOthersInvolvedAddMorePersons-addPerson2',
                formConfig,
                formValues
            )
                ? 'modpartens-rolle-2'
                : 'foerer';
            break;
        case 'om-modparten-2':
            nextPage = isAddAnotherPersonChecked(
                'doYouHaveInformationAboutOthersInvolvedAddMorePersons-addPerson3',
                formConfig,
                formValues
            )
                ? 'modpartens-rolle-3'
                : 'foerer';
            break;
        case 'om-modparten-3':
            nextPage = 'foerer';
            break;
        case 'foerer':
            if (getValueOrDefault(formConfig, formValues, 'whoDroveYourVehicle').value === 'iDroveTheVehicle') {
                nextPage = 'paavirkning';
            } else {
                nextPage = 'foerer-oplysninger';
            }
            break;
        case 'foerer-oplysninger':
            nextPage = 'paavirkning';
            break;
        case 'paavirkning':
            nextPage = 'dokumentation';
            break;

        case 'dokumentation':
            nextPage = 'politi';
            break;
        case 'politi':
            if (isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cvr')) {
                nextPage = 'find-vaerksted';
            } else {
                nextPage = 'virksomhedsoplysninger';
            }
            break;

        case 'find-vaerksted':
            nextPage = 'virksomhedsoplysninger';
            break;
        case 'virksomhedsoplysninger':
            nextPage = 'om-virksomheden';
            break;
        case 'om-virksomheden':
            nextPage = 'om-dig';
            break;
        case 'om-dig':
            nextPage = 'resume';
            break;

        case 'resume':
            nextPage = undefined;
            break;
        default:
            throw new Error('Unknown page: ' + curPageId);
    }

    return nextPage;
};

/**
 * Decides which damage page that are the next to show, depending on where in the form flow the user is, and which damage checkboxes that have been selected.
 * @param currentPageId The id of the current page
 * @param formConfig
 * @param formValues
 * @returns Next damage page to show.
 */
const getNextDamagePage = (
    currentPageId: string,
    formConfig: FormConfig,
    formValues: FormValues
): string | undefined => {
    const damageSelections = getWhatHasBeenDamaged(formConfig, formValues);

    switch (currentPageId) {
        case 'hvad-er-skadet':
            if (damageSelections.vehicle.isMyVehicleDamaged) {
                // The 'other' vehicle type can be selected in one of two ways:
                // 1. For a customer with a policy:
                //    Customer first selects 'Other' in the customerVehicle component, followed up by selecting 'Other' a second time in the radiobuttons list on the next page.
                //    Because the option selected in the radiobuttons list is not reset if user navigates back in the flow, we also need to check the registrationNumber value.
                // 2. For a customer without a policy (non policy case):
                //    The customerVehicle component will render vehicle options where 'Other' is one of the options.

                const isVehicleTypeOtherSelected =
                    getValueOrDefault(formConfig, formValues, 'vehicleTypeOther').value === 'other';

                // If a customer has selected 'other' as vehicle type -> skip the skadezone page
                if (isVehicleTypeOtherSelected) {
                    if (damageSelections.vehicle.isMyVehicleDamaged) return 'skader-andet-koeretoej';
                    if (damageSelections.persons.isDriverDamaged) return 'personskade';
                    if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
                    if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                    return 'oplysninger-om-modpart';
                } else {
                    if (damageSelections.vehicle.isMyVehicleDamaged) return 'skadezone';
                    if (damageSelections.persons.isDriverDamaged) return 'personskade';
                    if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
                    if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                }
            }
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'oplysninger-om-modpart';
        case 'skader-andet-koeretoej':
        case 'skadezone':
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';

            return 'oplysninger-om-modpart';
        case 'personskade':
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';

            return 'oplysninger-om-modpart';
        case 'oplysninger-om-passagerer':
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';

            return 'oplysninger-om-modpart';
        case 'skade-ting':
            const isReadyToFileAllThingsDamaged =
                getValueOrDefault(formConfig, formValues, 'areYouReadyToRegister').value === 'yes';

            if (isReadyToFileAllThingsDamaged) return 'genstandsregistrering';

            return 'oplysninger-om-modpart';
        case 'genstandsregistrering':
            return 'oplysninger-om-modpart';
    }
};

/**
 * Determines if a given 'add another person' checkbox element is selected or not.
 * @param elementId Id of the 'add another person' checkbox element
 * @param formConfig
 * @param formValues
 * @returns True if the 'add another person' checkbox is selected, else false.
 */
const isAddAnotherPersonChecked = (elementId: string, formConfig: FormConfig, formValues: FormValues): boolean => {
    const addAnotherPersonCheckbox = getValueOrDefault(formConfig, formValues, elementId);
    return addAnotherPersonCheckbox && addAnotherPersonCheckbox.length > 0
        ? addAnotherPersonCheckbox[0].value === 'yes'
        : false;
};
